import { Dialog, FormStatus, P } from '@dnb/eufemia';
import { stop } from '@dnb/eufemia/icons';
import type { ScopeRequestDto } from '@portals/shared/portal/ScopeRequestDto';
import { useCallback, useState } from 'react';

import { deletePendingScopeCreationRequest, deletePendingScopeDeletionRequest } from '@/api/api';

interface ScopeRequestDeleteProps {
  apiSlug: string;
  scopeRequestId: string;
  scopeName: string;
  requestType: ScopeRequestDto['requestType'];
  onSuccess: () => void;
}

export default function ScopeRequestDelete({
  apiSlug,
  scopeRequestId,
  scopeName,
  requestType,
  onSuccess,
}: ScopeRequestDeleteProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleScopeRequestDeletion = useCallback(async () => {
    setErrorMessage(null);
    const { error, response } =
      requestType === 'CREATE'
        ? await deletePendingScopeCreationRequest(apiSlug, scopeRequestId)
        : await deletePendingScopeDeletionRequest(apiSlug, scopeRequestId);
    if (response.ok) {
      setDialogOpen(false);
      onSuccess();
      return true;
    } else {
      setErrorMessage(error?.detail ?? 'Failed to delete scope request. Please try again.');
      return false;
    }
  }, [apiSlug, scopeRequestId, requestType, onSuccess]);

  return (
    <Dialog
      confirmText="Yes"
      confirmType="warning"
      declineText="No"
      description={
        <>
          <P>
            {requestType === 'CREATE' ? (
              <>
                Request to create scope <b>{scopeName}</b> will be canceled. If you need this scope in the future, you
                will have to create a new request.
              </>
            ) : (
              <>
                Request to delete scope <b>{scopeName}</b> will be canceled.
              </>
            )}
          </P>
          {errorMessage && (
            <FormStatus stretch top="x-small">
              {errorMessage}
            </FormStatus>
          )}
        </>
      }
      icon={stop}
      noAnimation
      onClose={() => setDialogOpen(false)}
      onConfirm={async ({ close }) => {
        const success = await handleScopeRequestDeletion();
        if (success) {
          close();
        }
      }}
      openState={dialogOpen}
      title="Are you sure you want to cancel the request?"
      triggerAttributes={{
        on_click: () => setDialogOpen(true),
        text: 'Cancel request',
        icon: stop,
        variant: 'tertiary',
      }}
      variant="confirmation"
    />
  );
}
